import { useGetOutgoingDealsQuery } from '@/app/api';
import { CabinetWrapper } from '@/shared/cabinet-wrapper';
import { EmptyDataPlaceholderProps } from '@/shared/ui/empty-data-placeholder';
import { Box, Button, Stack } from '@mui/material';
import { RequestsList } from './ui/requests-list';
import UITabs from '@/shared/ui/ui-tabs';
import useFilters from '@/app/hooks/useFilters';
import { useFactoringTypeFilter } from '@/app/hooks/useFactoringTypeFilter';
import { PageTitle } from '@/shared/page-title';
import { UITabItemLabel } from '../../shared/ui/ui-table/ui/tab-item-label';

import PlusIcon from '@/app/assets/icons/plus.svg?react';

import { useTranslation } from 'react-i18next';
import { UIPagination } from '@/shared/ui/ui-pagination';
import NewRequestModal from '@/shared/modal/NewRequestModal/NewRequestModal';
import { useRef } from 'react';
import { BaseModalRef } from '@/app/helpers/types';
import UIAutocomplete from '@/shared/ui/ui-autocmplete';
import { CURRENCY_OPTIONS } from '@/constants';
import { Currency } from '@/app/api/types';

const arrOfStatuses = [
  '',
  'active',
  'contractCreated',
  'completed',
  'declined',
  'draft',
  'underReview',
];

type LabelItem = {
  label: string;
  count: number;
};

export const SellerRequestsPage = () => {
  const { t } = useTranslation();

  const factoringTypeOptions = [
    {
      label: t('factoring_types.all'),
      value: 'all',
      filterValue: undefined,
    },
    {
      label: t('factoring_types.opened'),
      value: 'opened',
      filterValue: 'opened',
    },
    {
      label: t('factoring_types.closed'),
      value: 'closed',
      filterValue: 'closed',
    },
    {
      label: t('factoring_types.reverse'),
      value: 'reverse',
      filterValue: 'reverse',
    },
    {
      label: t('factoring_types.export'),
      value: 'export',
      filterValue: 'export',
    },
    {
      label: t('factoring_types.import'),
      value: 'import',
      filterValue: 'import',
    },
  ];

  const { filters, updateFilters } = useFilters({
    saveToUrl: true,
    defaultFilters: {
      page: 1,
      pageSize: 10,
      tab: 0,
      factoringType: 'all',
      currency: 'UZS',
    },
  });

  const factoringType = useFactoringTypeFilter(
    filters.factoringType,
    (value) => updateFilters({ factoringType: value })
  );

  const { data, isLoading } = useGetOutgoingDealsQuery({
    ...filters,
    status: arrOfStatuses[filters.tab || 0],
    page: Number(filters.page),
    pageSize: Number(filters.pageSize),
    factoringType: factoringTypeOptions.find(
      (item) => item.value === factoringType
    )?.filterValue,
    currency: filters.currency as Currency,
  });

  const newRequestModalRef = useRef<BaseModalRef>(null);

  const title = t('no_seller_requests_yet');
  const subtitle = t('add_new_financing_request');
  const hasAnyDoc = data?.data.length;
  const emptyPlaceholderProps: EmptyDataPlaceholderProps = {
    title,
    subtitle,
    isLoading,
    isEmpty: !hasAnyDoc,
    endAdornment: (
      <Button
        onClick={() => newRequestModalRef.current?.open()}
        endIcon={<PlusIcon />}
      >
        {t('add_bid')}
      </Button>
    ),
  };

  const labels: LabelItem[] = [
    {
      label: t('requests_tabs.all'),
      count:
        (data?.dealsCount['draft'] || 0) +
        (data?.dealsCount['active'] || 0) +
        (data?.dealsCount['contractCreated'] || 0) +
        (data?.dealsCount['underReview'] || 0) +
        (data?.dealsCount['completed'] || 0) +
        (data?.dealsCount['declined'] || 0),
    },
    {
      label: t('requests_tabs.active'),
      count: data?.dealsCount['active'] || 0,
    },
    {
      label: t('incoming_requests_tabs.waiting_for_sign'),
      count: data?.dealsCount['contractCreated'] || 0,
    },

    {
      label: t('requests_tabs.successfull'),
      count: data?.dealsCount['completed'] || 0,
    },
    // {
    //   label: t('requests_tabs.contract_formed'),
    //   count: data?.dealsCount['contractCreatedByFactor'] || 0,
    // },
    {
      label: t('requests_tabs.rejected'),
      count: data?.dealsCount['declined'] || 0,
    },
    {
      label: t('requests_tabs.drafts'),
      count: data?.dealsCount['draft'] || 0,
    },
    {
      label: t('requests_tabs.underReview'),
      count: data?.dealsCount['underReview'] || 0,
    },
  ];

  const tabs = labels.map((item) => ({
    label: <UITabItemLabel label={item.label} count={item.count} />,
    component: (
      <>
        <Stack direction='row' spacing={2} mb={2}>
          <UIAutocomplete
            value={factoringTypeOptions.find(
              (item) => item.value === factoringType
            )}
            options={factoringTypeOptions}
            getOptionLabel={(option) => option.label}
            onChange={(_e, value) =>
              updateFilters({ factoringType: value?.value })
            }
            TextFieldProps={{
              label: t('factoring_type'),
              sx: { width: 240 },
            }}
            disableClearable
          />
          <UIAutocomplete
            value={CURRENCY_OPTIONS.find(
              (item) => item.value === filters.currency
            )}
            options={CURRENCY_OPTIONS}
            TextFieldProps={{
              label: t('currency'),
              sx: { width: 240 },
            }}
            getOptionLabel={(option) => {
              return t(option.label);
            }}
            onChange={(_event, value) => {
              updateFilters({ currency: value?.value as Currency });
            }}
            disableClearable
          />
        </Stack>

        <RequestsList
          data={data?.data || []}
          emptyPlaceholderProps={emptyPlaceholderProps}
        />
      </>
    ),
  }));

  return (
    <CabinetWrapper>
      <PageTitle
        title={t('my_requests')}
        noBack
        sx={{
          alignItems: 'flex-start',
        }}
        rightComponent={
          <Button
            endIcon={<PlusIcon />}
            onClick={() => newRequestModalRef.current?.open()}
          >
            {t('add_bid')}
          </Button>
        }
      />
      <UITabs
        value={Number(filters.tab || 0)}
        onChange={(_e, value) => updateFilters({ tab: value, page: 1 })}
        sx={{
          mt: 2,
        }}
        tabs={tabs}
      />

      {!!hasAnyDoc && (
        <UIPagination
          page={Number(filters.page)}
          count={data?.totalPages || 0}
          onChange={(_e, value) => updateFilters({ page: value })}
          sizes={{
            currentRowsPerPage: filters.pageSize,
            onRowsPerPageChange: (value) =>
              updateFilters({ pageSize: Number(value), page: 1 }),
          }}
        />
      )}
      <NewRequestModal ref={newRequestModalRef} />
    </CabinetWrapper>
  );
};
